import { FormEvent, useState } from 'react'
import { Helmet } from 'react-helmet'
import { PatternFormat } from 'react-number-format'
import Header from '../components/header'
import { submitContactForm } from '../api/api'
import {
    ConfirmationActions,
    ConfirmationMessage,
    ContactForm,
    ContactFormBand,
    ContactFormCard,
    ContactHero,
    ContactInner,
    ContactInput,
    ContactIntro,
    ContactPage,
    ContactTextarea,
    Field,
    FieldError,
    FieldLabel,
    FormFooter,
    HomeLink,
    RequiredLegend,
    SubmitButton,
} from '../components/styles/Contact.styled'

type FormErrors = {
    name?: string
    email?: string
    phone?: string
    message?: string
    form?: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_PATTERN = /^\(\d{3}\) \d{3}-\d{4}$/

const requiredMessage = 'This field is required.'
const SUBMIT_SUCCESS =
    "Thanks for reaching out. We've received your message and will get back to you shortly."

const isApiSuccess = (res: { code?: number | string } | null | undefined) =>
    Number(res?.code) === 200

const polishSubmitError = (apiMessage?: string) => {
    const raw = (apiMessage || '').toLowerCase()
    if (raw.includes('valid email') || raw.includes('invalid email')) {
        return 'Please enter a valid email address.'
    }
    return 'We could not send your message. Please try again.'
}

export const Contact = () => {
    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [email, setEmail] = useState('')
    const [message, setMessage] = useState('')
    const [errors, setErrors] = useState<FormErrors>({})
    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    const validateEmail = () => {
        const trimmedEmail = email.trim()
        if (!trimmedEmail) return requiredMessage
        if (!EMAIL_PATTERN.test(trimmedEmail)) return 'Please enter a valid email address.'
        return undefined
    }

    const validateForm = (): FormErrors => {
        const next: FormErrors = {}
        const trimmedName = name.trim()
        const trimmedPhone = phone.trim()
        const emailError = validateEmail()

        if (!trimmedName) next.name = requiredMessage
        if (emailError) next.email = emailError
        if (!message.trim()) next.message = requiredMessage
        if (trimmedPhone && !PHONE_PATTERN.test(trimmedPhone)) {
            next.phone = 'Please enter a valid phone number.'
        }

        return next
    }

    const focusFirstInvalid = () => {
        const firstInvalid = document.querySelector<HTMLElement>('[data-invalid="true"]')
        firstInvalid?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const nextErrors = validateForm()
        setErrors(nextErrors)

        if (Object.keys(nextErrors).length > 0) {
            focusFirstInvalid()
            return
        }

        setSubmitting(true)
        setErrors((current) => ({ ...current, form: undefined }))

        try {
            const res = await submitContactForm({
                name: name.trim(),
                phone: phone.trim(),
                email: email.trim(),
                message: message.trim(),
            })

            if (isApiSuccess(res)) {
                setSubmitted(true)
            } else {
                setErrors((current) => ({
                    ...current,
                    form: polishSubmitError(res?.message),
                }))
            }
        } catch {
            setErrors((current) => ({
                ...current,
                form: polishSubmitError(),
            }))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>Contact Us - TEXAS ENERGY GROUP</title>
                <meta
                    name="description"
                    content="Reach out to us today, and together, we’ll power the future of your residential energy needs."
                />
            </Helmet>
            <Header />
            <ContactPage id="main-content">
                <ContactHero>
                    <ContactInner>
                        <ContactIntro>
                            <h1>Send Us A Message</h1>
                            <p>
                                Reach out to us today, and together, we’ll power the future of
                                your residential energy needs.
                            </p>
                        </ContactIntro>
                    </ContactInner>

                    <ContactFormBand>
                        <ContactInner>
                            <ContactFormCard>
                                {submitted ? (
                                    <>
                                        <ConfirmationMessage role="status">
                                            {SUBMIT_SUCCESS}
                                        </ConfirmationMessage>
                                        <ConfirmationActions>
                                            <HomeLink to="/">Back to Home</HomeLink>
                                        </ConfirmationActions>
                                    </>
                                ) : (
                                    <ContactForm onSubmit={handleSubmit} noValidate>
                                        <RequiredLegend>
                                            &quot;<span className="req">*</span>&quot; indicates
                                            required fields
                                        </RequiredLegend>

                                        <Field>
                                            <FieldLabel htmlFor="contact-name">
                                                Name<span className="req">*</span>
                                            </FieldLabel>
                                            <ContactInput
                                                id="contact-name"
                                                name="name"
                                                type="text"
                                                autoComplete="name"
                                                value={name}
                                                onChange={(event) => setName(event.target.value)}
                                                aria-required="true"
                                                aria-invalid={Boolean(errors.name)}
                                                $invalid={Boolean(errors.name)}
                                                data-invalid={errors.name ? 'true' : undefined}
                                                disabled={submitting}
                                            />
                                            {errors.name && <FieldError>{errors.name}</FieldError>}
                                        </Field>

                                        <Field>
                                            <FieldLabel htmlFor="contact-phone">Phone</FieldLabel>
                                            <PatternFormat
                                                id="contact-phone"
                                                name="phone"
                                                type="tel"
                                                format="(###) ###-####"
                                                placeholder="(999) 999-9999"
                                                autoComplete="tel"
                                                value={phone}
                                                onValueChange={(values) =>
                                                    setPhone(values.formattedValue)
                                                }
                                                customInput={ContactInput}
                                                aria-invalid={Boolean(errors.phone)}
                                                $invalid={Boolean(errors.phone)}
                                                data-invalid={errors.phone ? 'true' : undefined}
                                                disabled={submitting}
                                            />
                                            {errors.phone && (
                                                <FieldError>{errors.phone}</FieldError>
                                            )}
                                        </Field>

                                        <Field>
                                            <FieldLabel htmlFor="contact-email">
                                                Email<span className="req">*</span>
                                            </FieldLabel>
                                            <ContactInput
                                                id="contact-email"
                                                name="email"
                                                type="email"
                                                autoComplete="email"
                                                value={email}
                                                onChange={(event) => setEmail(event.target.value)}
                                                aria-required="true"
                                                aria-invalid={Boolean(errors.email)}
                                                $invalid={Boolean(errors.email)}
                                                data-invalid={errors.email ? 'true' : undefined}
                                                disabled={submitting}
                                            />
                                            {errors.email && (
                                                <FieldError>{errors.email}</FieldError>
                                            )}
                                        </Field>

                                        <Field>
                                            <FieldLabel htmlFor="contact-message">
                                                Message<span className="req">*</span>
                                            </FieldLabel>
                                            <ContactTextarea
                                                id="contact-message"
                                                name="message"
                                                rows={10}
                                                value={message}
                                                onChange={(event) =>
                                                    setMessage(event.target.value)
                                                }
                                                aria-required="true"
                                                aria-invalid={Boolean(errors.message)}
                                                $invalid={Boolean(errors.message)}
                                                data-invalid={errors.message ? 'true' : undefined}
                                                disabled={submitting}
                                            />
                                            {errors.message && (
                                                <FieldError>{errors.message}</FieldError>
                                            )}
                                        </Field>

                                        {errors.form && <FieldError>{errors.form}</FieldError>}

                                        <FormFooter>
                                            <SubmitButton type="submit" disabled={submitting}>
                                                Submit
                                            </SubmitButton>
                                        </FormFooter>
                                    </ContactForm>
                                )}
                            </ContactFormCard>
                        </ContactInner>
                    </ContactFormBand>
                </ContactHero>
            </ContactPage>
        </>
    )
}
